import { calcPos, type Position } from '@wertermittlung/shared';
import { el, fmt, numFormat } from '../../lib/dom.js';
import { summen } from '../../lib/state.js';
import { fotoRow } from './fotos.js';
import { openPicker } from './picker.js';
import { fieldNum, fieldSlider, fieldText } from './widgets.js';
import type { WizardCtx } from './types.js';

type Kategorie = 'neben' | 'kulturen' | 'zier';

interface KategorieMeta {
  schritt: number;
  titel: string;
  beschreibung: string;
  addLabel: string;
  leer: string;
  pflege?: 'kulturen' | 'zier';
}

const META: Record<Kategorie, KategorieMeta> = {
  neben: {
    schritt: 3,
    titel: 'Nebenanlagen',
    beschreibung:
      'Zäune, Wege, Wasser, Gewächshaus & Co. – aus dem Katalog wählen, Menge und Zustand angeben. Was es nicht gibt, lassen Sie einfach weg.',
    addLabel: '+ Nebenanlage hinzufügen',
    leer: 'Noch keine Nebenanlagen erfasst.',
  },
  kulturen: {
    schritt: 4,
    titel: 'Gärtnerische Kulturen',
    beschreibung:
      'Obstgehölze, Beerenobst, Gemüse. Die Wertminderung berücksichtigt Standort, Pflanzabstand, Vitalität und Pflege (0–100 %).',
    addLabel: '+ Kultur hinzufügen',
    leer: 'Noch keine Kulturen erfasst.',
    pflege: 'kulturen',
  },
  zier: {
    schritt: 5,
    titel: 'Zierbegrünung',
    beschreibung:
      'Rosen, Ziergehölze, Hecken, Stauden und Rasen. Höchstwerte aus der Richtlinie werden automatisch gedeckelt.',
    addLabel: '+ Zierbegrünung hinzufügen',
    leer: 'Noch keine Zierbegrünung erfasst.',
    pflege: 'zier',
  },
};

export function renderPositionen(
  ctx: WizardCtx,
  container: HTMLElement,
  kategorie: Kategorie,
): void {
  const meta = META[kategorie];
  const positionen = ctx.p.data.pos[kategorie];

  const head = el('div', { class: 'step-head' });
  head.append(
    el('div', { class: 'eyebrow' }, `Schritt ${meta.schritt} von 7`),
    el('h2', {}, meta.titel),
    el('p', {}, meta.beschreibung),
  );
  container.append(head);

  const liste = el('div');
  if (positionen.length === 0) liste.append(el('div', { class: 'list-empty' }, meta.leer));
  positionen.forEach((p, i) => liste.append(positionsKarte(ctx, positionen, p, i)));
  container.append(liste);

  const add = el('button', { class: 'btn btn-add', type: 'button' }, meta.addLabel);
  add.addEventListener('click', () =>
    openPicker(ctx.katalog, kategorie, (eintrag) => {
      positionen.push({
        id: crypto.randomUUID(),
        name: eintrag.n,
        einheit: eintrag.e,
        preis: eintrag.p,
        menge: 1,
        minderung: 0,
        krit: '',
        g: (eintrag.g as Position['g']) ?? null,
        fotos: [],
      });
      ctx.save();
      ctx.recalc();
      ctx.rerender();
    }),
  );
  container.append(add);

  // Summenkarte
  const karte = el('div', { class: 'card', style: 'margin-top:14px' });
  const s = () => summen(ctx.p);
  const capBox = el('div');
  const zeilen: [string, () => string][] = [];
  if (kategorie === 'neben') {
    zeilen.push(['Summe Nebenanlagen', () => fmt(s().neben)]);
  } else if (kategorie === 'kulturen') {
    zeilen.push(
      ['Zwischensumme', () => fmt(s().kulturenZwischen)],
      ['Summe gärtnerische Kulturen', () => fmt(s().kulturen)],
    );
  } else {
    zeilen.push(
      ['Zwischensumme (nach Höchstwerten)', () => fmt(s().zierZwischen)],
      ['Summe Zierbegrünung', () => fmt(s().zier)],
    );
    karte.append(capBox);
  }

  if (meta.pflege) {
    const key = meta.pflege;
    karte.append(
      fieldSlider(
        'Minderung aufgrund von Pflegerückständen',
        () => ctx.p.data.pflege[key],
        (v) => (ctx.p.data.pflege[key] = v),
        () => {
          ctx.save();
          ctx.recalc();
          updateSummen();
        },
        { max: 60 },
      ),
    );
  }

  const sumEls: HTMLElement[] = zeilen.map(([label], i) => {
    const zeile = el('div', { class: 'sumline' + (i === zeilen.length - 1 ? ' total' : '') });
    zeile.append(el('span', {}, label), el('strong'));
    karte.append(zeile);
    return zeile;
  });

  const updateSummen = () => {
    zeilen.forEach(([, wert], i) => {
      const strong = sumEls[i]?.querySelector('strong');
      if (strong) strong.textContent = wert();
    });
    if (kategorie === 'zier') {
      capBox.replaceChildren();
      for (const c of s().gedeckelt) {
        capBox.append(
          el(
            'div',
            { class: 'capnote' },
            `Höchstwert erreicht: ${c.label} sind auf ${fmt(c.max)} gedeckelt (erfasst: ${fmt(c.ist)}).`,
          ),
        );
      }
    }
  };
  updateSummen();
  container.append(karte);

  // Positions-Widgets aktualisieren die Summen mit
  container.addEventListener('input', updateSummen);
}

function positionsKarte(
  ctx: WizardCtx,
  positionen: Position[],
  p: Position,
  index: number,
): HTMLElement {
  const card = el('div', { class: 'poscard' });
  const head = el('div', { class: 'poshead' });
  const titel = el('strong', {}, p.name + ' ');
  titel.append(el('span', { class: 'einheit' }, `je ${p.einheit}`));
  head.append(titel);
  const remove = el(
    'button',
    { class: 'btn-x', type: 'button', 'aria-label': 'Position entfernen' },
    '✕',
  );
  remove.addEventListener('click', () => {
    positionen.splice(index, 1);
    ctx.save();
    ctx.recalc();
    ctx.rerender();
  });
  head.append(remove);
  card.append(head);

  const result = el('div', { class: 'posresult' });
  const path = el('span', { class: 'calcpath' });
  const wert = el('strong');
  result.append(path, wert);
  const update = () => {
    path.textContent = `${numFormat(p.menge)} × ${numFormat(p.preis)} € × ${numFormat(ctx.p.faktor)} − ${p.minderung} %`;
    wert.textContent = fmt(calcPos(p, ctx.p.faktor));
  };
  const onWert = () => {
    ctx.save();
    ctx.recalc();
    update();
  };

  const grid = el('div', { class: 'grid' });
  grid.append(
    fieldNum(
      `Menge (${p.einheit})`,
      () => p.menge,
      (v) => (p.menge = v ?? 0),
      onWert,
    ),
    fieldNum(
      'Richtwert',
      () => p.preis,
      (v) => (p.preis = v ?? 0),
      onWert,
      {
        unit: `€/${p.einheit}`,
      },
    ),
    fieldSlider(
      'Wertminderung (Alter, Zustand, Standort, Pflege)',
      () => p.minderung,
      (v) => (p.minderung = v),
      onWert,
      {
        wide: true,
      },
    ),
    fieldText(
      'Bewertungskriterien / Sorte',
      () => p.krit,
      (v) => (p.krit = v),
      ctx.save,
      {
        wide: true,
        placeholder: 'z. B. Jonagold, guter Schnitt',
      },
    ),
  );
  card.append(grid, fotoRow(ctx, p.fotos), result);
  update();
  return card;
}
